import { ref, computed, watch, onMounted, onUnmounted } from 'vue';
import Gun from 'gun';
import 'gun/sea';
import { getTalkFlowCore } from '@/composables/TalkFlowCore';
import { toastController } from '@ionic/vue';
import dayjs from 'dayjs';
import router from '@/router';

interface GroupChatKeyPair {
  pub: string;
  priv: string;
  epub: string;
  epriv: string;
  alias?: string;
}

interface GroupChatGroup {
  name: string;
  pub: string;
  pair: GroupChatKeyPair;
}

interface GroupChatMessage {
  id: string;
  sender: string;
  senderPub: string;
  text: string;
  timestamp: number;
  formattedTime: string;
  status?: 'sent' | 'pending' | 'failed';
  isSending?: boolean;
  duration?: number;
}

interface GroupChatMember {
  pubKey: string;
  alias: string;
  lastActive: number;
  joinedAt: number;
  isOnline: boolean;
}

interface GroupChatVote {
  voterId: string;
  vote: 'agree' | 'disagree';
  timestamp: number;
}

interface GroupChatClearSignal {
  initiator: string;
  timestamp: number;
  cleared: boolean;
}

interface GroupChatSession {
  groupPub: string;
  groupName: string;
  isRead: boolean;
  lastActivity: number;
  previewMessage?: GroupChatMessage;
}

// 🆕 环境检测函数
function detectPlatform(): 'ios' | 'android' | 'browser' {
  const userAgent = navigator.userAgent || navigator.vendor || (window as any).opera;
  
  // 检测iOS
  if (/iPad|iPhone|iPod/.test(userAgent) && !(window as any).MSStream) {
    return 'ios';
  }
  
  // 检测Android  
  if (/android/i.test(userAgent)) {
    return 'android';
  }
  
  // 其他情况视为浏览器
  return 'browser';
}

// 🆕 智能存储类 - 根据平台选择存储方式
class SmartGroupStorage {
  private platform: 'ios' | 'android' | 'browser';
  private useLocalStorage: boolean;
  
  constructor() {
    this.platform = detectPlatform();
    // iOS/Android 优先使用SQLite，浏览器使用localStorage
    this.useLocalStorage = this.platform === 'browser';
    
   // console.log('🔍 检测到平台:', this.platform);
    //console.log('📱 存储策略:', this.useLocalStorage ? 'localStorage' : 'SQLite');
  }
  
  async saveGroups(groups: GroupChatGroup[]): Promise<void> {
   // console.log('💾 开始保存群组...');
    //console.log('📊 保存群组数量:', groups.length);
    
    if (this.useLocalStorage) {
      return this.saveGroupsToLocalStorage(groups);
    } else {
      return this.saveGroupsToSQLite(groups);
    }
  }
  
  async loadGroups(): Promise<GroupChatGroup[]> {
   // console.log('📂 开始加载群组...');
    
    if (this.useLocalStorage) {
      return this.loadGroupsFromLocalStorage();
    } else {
      return this.loadGroupsFromSQLite();
    }
  }
  
  async saveSessions(sessions: GroupChatSession[]): Promise<void> {
  //  console.log('💾 开始保存会话...');
   // console.log('📊 保存会话数量:', sessions.length);
    
    if (this.useLocalStorage) {
      return this.saveSessionsToLocalStorage(sessions);
    } else {
      return this.saveSessionsToSQLite(sessions);
    }
  }
  
  async loadSessions(): Promise<GroupChatSession[]> {
   // console.log('📂 开始加载会话...');
    
    if (this.useLocalStorage) {
      return this.loadSessionsFromLocalStorage();
    } else {
      return this.loadSessionsFromSQLite();
    }
  }
  
  // localStorage实现（浏览器）
  private saveGroupsToLocalStorage(groups: GroupChatGroup[]): Promise<void> {
    return new Promise((resolve, reject) => {
      try {
        // 数据压缩：只保留必要字段
        const compactGroups = groups.map(group => ({
          name: group.name,
          pub: group.pub,
          pair: {
            pub: group.pair.pub,
            priv: group.pair.priv,
            epub: group.pair.epub,
            epriv: group.pair.epriv
          }
        }));
        
        const data = {
          groups: compactGroups,
          timestamp: Date.now(),
          version: '1.0'
        };
        
        localStorage.setItem('talkflow_group_chats', JSON.stringify(data));
      //  console.log('✅ localStorage保存成功');
        resolve();
      } catch (error) {
        console.error('❌ localStorage保存失败:', error);
        reject(error);
      }
    });
  }
  
  private loadGroupsFromLocalStorage(): Promise<GroupChatGroup[]> {
    return new Promise((resolve) => {
      try {
        const data = localStorage.getItem('talkflow_group_chats');
        if (!data) {
        //  console.log('📝 localStorage中没有群组数据');
          resolve([]);
          return;
        }
        
        const parsed = JSON.parse(data);
        const groups = parsed.groups || [];
       // console.log('✅ localStorage加载成功，群组数量:', groups.length);
        resolve(groups);
      } catch (error) {
       // console.error('❌ localStorage加载失败:', error);
        resolve([]);
      }
    });
  }
  
  private saveSessionsToLocalStorage(sessions: GroupChatSession[]): Promise<void> {
    return new Promise((resolve, reject) => {
      try {
        const data = {
          sessions: sessions,
          timestamp: Date.now(),
          version: '1.0'
        };
        
        localStorage.setItem('talkflow_group_sessions', JSON.stringify(data));
       // console.log('✅ localStorage会话保存成功');
        resolve();
      } catch (error) {
        console.error('❌ localStorage会话保存失败:', error);
        reject(error);
      }
    });
  }
  
  private loadSessionsFromLocalStorage(): Promise<GroupChatSession[]> {
    return new Promise((resolve) => {
      try {
        const data = localStorage.getItem('talkflow_group_sessions');
        if (!data) {
         // console.log('📝 localStorage中没有会话数据');
          resolve([]);
          return;
        }
        
        const parsed = JSON.parse(data);
        const sessions = parsed.sessions || [];
       // console.log('✅ localStorage会话加载成功，会话数量:', sessions.length);
        resolve(sessions);
      } catch (error) {
        console.error('❌ localStorage会话加载失败:', error);
        resolve([]);
      }
    });
  }
  
  // SQLite实现（iOS/Android）
  private async saveGroupsToSQLite(groups: GroupChatGroup[]): Promise<void> {
    try {
      // 检查SQLite是否可用
      if (typeof (window as any).SQLitePlugin === 'undefined') {
        console.warn('⚠️ SQLite不可用，降级到localStorage');
        return this.saveGroupsToLocalStorage(groups);
      }
      
      // TODO: 实现SQLite保存逻辑
      console.log('🔧 SQLite群组保存功能开发中，使用localStorage降级');
      return this.saveGroupsToLocalStorage(groups);
    } catch (error) {
      console.error('❌ SQLite保存失败，降级到localStorage:', error);
      return this.saveGroupsToLocalStorage(groups);
    }
  }
  
  private async loadGroupsFromSQLite(): Promise<GroupChatGroup[]> {
    try {
      // 检查SQLite是否可用
      if (typeof (window as any).SQLitePlugin === 'undefined') {
        console.warn('⚠️ SQLite不可用，降级到localStorage');
        return this.loadGroupsFromLocalStorage();
      }
      
      // TODO: 实现SQLite加载逻辑
      console.log('🔧 SQLite群组加载功能开发中，使用localStorage降级');
      return this.loadGroupsFromLocalStorage();
    } catch (error) {
      console.error('❌ SQLite加载失败，降级到localStorage:', error);
      return this.loadGroupsFromLocalStorage();
    }
  }
  
  private async saveSessionsToSQLite(sessions: GroupChatSession[]): Promise<void> {
    try {
      // 检查SQLite是否可用
      if (typeof (window as any).SQLitePlugin === 'undefined') {
        console.warn('⚠️ SQLite不可用，降级到localStorage');
        return this.saveSessionsToLocalStorage(sessions);
      }
      
      // TODO: 实现SQLite会话保存逻辑
      console.log('🔧 SQLite会话保存功能开发中，使用localStorage降级');
      return this.saveSessionsToLocalStorage(sessions);
    } catch (error) {
      console.error('❌ SQLite会话保存失败，降级到localStorage:', error);
      return this.saveSessionsToLocalStorage(sessions);
    }
  }
  
  private async loadSessionsFromSQLite(): Promise<GroupChatSession[]> {
    try {
      // 检查SQLite是否可用
      if (typeof (window as any).SQLitePlugin === 'undefined') {
        console.warn('⚠️ SQLite不可用，降级到localStorage');
        return this.loadSessionsFromLocalStorage();
      }
      
      // TODO: 实现SQLite会话加载逻辑
      console.log('🔧 SQLite会话加载功能开发中，使用localStorage降级');
      return this.loadSessionsFromLocalStorage();
    } catch (error) {
      console.error('❌ SQLite会话加载失败，降级到localStorage:', error);
      return this.loadSessionsFromLocalStorage();
    }
  }
}

// 创建存储实例
const groupStorage = new SmartGroupStorage();

// State moved outside the composable function to create a singleton-like state
const newGroupName = ref('');
const joinGroupKey = ref('');
const groups = ref<GroupChatGroup[]>([]);
const currentGroup = ref<string | null>(null);
const currentGroupName = ref('');
const messagesByGroup = ref<{ [groupPub: string]: GroupChatMessage[] }>({});
const membersByGroup = ref<{ [groupPub: string]: GroupChatMember[] }>({});
const votesByGroup = ref<{ [groupPub: string]: GroupChatVote[] }>({});
const newMessage = ref('');
const tempKeyPair = ref<GroupChatKeyPair | null>(null);
const groupSessions = ref<GroupChatSession[]>([]);
const messageListeners: { [groupPub: string]: any } = {};
const memberListeners: { [groupPub: string]: any } = {};
const voteListeners: { [groupPub: string]: any } = {};
const signalListeners: { [groupPub: string]: any } = {};
const lastMessageTimestamps: { [groupPub: string]: number | undefined } = {};
const groupNameListeners: { [groupPub: string]: any } = {};
let activityInterval: NodeJS.Timeout | null = null;

export const useGroupChat = () => {
  const chatFlow = getTalkFlowCore();
  const { currentUserPub, currentUserAlias, gun, encryptMessages, decryptMessages, triggerLightHaptic } = chatFlow;

  // 重新定义群聊相关的响应式变量，避免类型冲突

  const safeUserPub = computed(() => currentUserPub.value || 'No Name');
  const safeUserAlias = computed(() => currentUserAlias.value || 'No Name');

  const agreeCount = computed(() => {
    if (!currentGroup.value) return 0;
    return (votesByGroup.value[currentGroup.value] || []).filter((v) => v.vote === 'agree').length;
  });

  const canClearChat = computed(() => {
    if (!currentGroup.value) return false;
    return agreeCount.value === (membersByGroup.value[currentGroup.value] || []).length && (membersByGroup.value[currentGroup.value] || []).length > 0;
  });

  const formatMessageTime = (timestamp: number): string => {
    return dayjs(timestamp).format('YYYY-MM-DD HH:mm:ss');
  };

  const copyKeyPair = async (pair: GroupChatKeyPair) => {
    try {
      await navigator.clipboard.writeText(JSON.stringify(pair, null, 2));
      const toast = await toastController.create({
        message: 'Copied',
        duration: 2000,
        position: 'top',
      });
      await toast.present();
    } catch (error) {
      // Handle error silently
    }
  };

  // 🔧 简化：直接从localStorage加载
  const loadGroups = async () => {
   // console.log('📚 开始加载群组...');
    try {
      // 加载群聊列表
      const loadedGroups = await groupStorage.loadGroups();
      groups.value = loadedGroups;
      
      // 加载群聊会话
      const loadedSessions = await groupStorage.loadSessions();
      groupSessions.value = loadedSessions;
      
     // console.log('✅ 群组和会话加载完成');
     // console.log('📊 群组数量:', groups.value.length);
     // console.log('📊 会话数量:', groupSessions.value.length);
      
      // 🆕 为所有已加载的群聊设置名称监听器
     // console.log('🔊 为已有群聊设置名称监听器...');
      groups.value.forEach(group => {
        listenToGroupNameChanges(group.pub);
      });
     // console.log('✅ 群聊名称监听器设置完成');
      
      // 显示存储统计
      const stats = {
        groupsSize: localStorage.getItem('talkflow_group_chats')?.length || 0,
        sessionsSize: localStorage.getItem('talkflow_group_sessions')?.length || 0,
        totalSize: localStorage.getItem('talkflow_group_chats')?.length || 0 + localStorage.getItem('talkflow_group_sessions')?.length || 0
      };
      console.log('📊 存储统计:', {
        groups: `${(stats.groupsSize / 1024).toFixed(2)} KB`,
        sessions: `${(stats.sessionsSize / 1024).toFixed(2)} KB`,
        total: `${(stats.totalSize / 1024).toFixed(2)} KB`
      });
      
    } catch (error) {
      console.error('❌ 加载群组失败:', error);
      // 重置为空数组
      groups.value = [];
      groupSessions.value = [];
    }
  };

  // 🔧 简化：直接保存到localStorage
  const saveGroups = async () => {
   // console.log('💾 开始保存群组...');
    try {
      await groupStorage.saveGroups(groups.value);
  //    console.log('🎉 群组保存完成!');
    } catch (error) {
      console.error('❌ 保存群组失败:', error);
      throw error;
    }
  };

  // 🔧 简化：直接保存会话到localStorage
  const saveSessions = async () => {
   // console.log('💾 开始保存会话...');
    try {
      await groupStorage.saveSessions(groupSessions.value);
     // console.log('✅ 会话保存完成!');
    } catch (error) {
      console.error('❌ 保存会话失败:', error);
      throw error;
    }
  };

  const generateKeyPair = async (): Promise<GroupChatKeyPair> => {
    try {
      const pair = await Promise.race([
        Gun.SEA.pair(),
        new Promise((_, reject) =>
          setTimeout(() => reject(new Error('密钥生成超时')), 5000)
        ),
      ]);
              return pair as GroupChatKeyPair;
    } catch (error) {
      throw error;
    }
  };

  const updateMemberActivity = async (groupPub: string) => {
    if (!safeUserPub.value || !safeUserAlias.value) return;
    const group = groups.value.find((g) => g.pub === groupPub);
    if (!group) return;
    const memberData = {
      alias: safeUserAlias.value,
      lastActive: Date.now(),
      joinedAt: Date.now(),
    };
    await gun.get(`group_${groupPub}_members`).get(safeUserPub.value).put(memberData);
  };

  const initializeVote = async (groupPub: string) => {
    if (!safeUserPub.value) return;
    await gun.get(`group_${groupPub}_clear_votes`).get(safeUserPub.value).put({ agreed: false });
  };

  let activityInterval: NodeJS.Timeout | null = null;
  const startActivityUpdates = (groupPub: string) => {
    if (activityInterval) {
      clearInterval(activityInterval);
    }
    activityInterval = setInterval(() => {
      updateMemberActivity(groupPub);
    }, 30000);
  };

  const stopActivityUpdates = () => {
    if (activityInterval) {
      clearInterval(activityInterval);
      activityInterval = null;
    }
  };

  // 🆕 群聊名称实时同步相关变量和监听器
  const groupNameListeners: { [groupPub: string]: any } = {};

  // 🆕 将群聊名称保存到Gun网络（英文命名）
  const saveGroupNameToNetwork = async (groupPub: string, groupName: string) => {
    try {
     // console.log('💾 Saving group name to Gun network:', { groupPub, groupName });
      const groupNameData = {
        name: groupName,
        createdAt: Date.now(),
        updatedAt: Date.now(),
        version: '1.0'
      };
      
      // 使用专门的节点存储群聊名称
      await gun.get(`group_name_${groupPub}`).put(groupNameData);
      
      // 兼容性：也保存到旧的meta节点
      await gun.get(`group_${groupPub}_meta`).put({
        name: groupName,
        timestamp: Date.now()
      });
      
    //  console.log('✅ Group name saved to network successfully');
    } catch (error) {
      console.error('❌ Failed to save group name to network:', error);
      throw error;
    }
  };

  // 🆕 从Gun网络获取群聊名称
  const getGroupNameFromNetwork = async (groupPub: string): Promise<string> => {
    try {
    //  console.log('🔍 Getting group name from Gun network:', groupPub);
      
      // 优先从新的群聊名称节点获取
      const nameNode = gun.get(`group_name_${groupPub}`);
      const nameData = await new Promise<any>((resolve) => {
        nameNode.once((data) => resolve(data));
        // 设置超时，避免无限等待
        setTimeout(() => resolve(null), 3000);
      });
      
      if (nameData?.name) {
        console.log('✅ Got group name from new node:', nameData.name);
        return nameData.name;
      }
      
      // 如果新节点没有数据，尝试从旧的meta节点获取
      const metaNode = gun.get(`group_${groupPub}_meta`);
      const metaData = await new Promise<any>((resolve) => {
        metaNode.once((data) => resolve(data));
        setTimeout(() => resolve(null), 3000);
      });
      
      if (metaData?.name) {
        console.log('✅ Got group name from meta node:', metaData.name);
        return metaData.name;
      }
      
      // 如果都没有，返回默认名称
      const defaultName = `Group_${groupPub.slice(0, 8)}`;
      console.log('⚠️ No group name found, using default:', defaultName);
      return defaultName;
      
    } catch (error) {
      console.error('❌ Failed to get group name from network:', error);
      return `Group_${groupPub.slice(0, 8)}`;
    }
  };

  // 🆕 监听群聊名称变化（实时同步）
  const listenToGroupNameChanges = (groupPub: string) => {
    console.log('🔊 Setting up group name change listener for:', groupPub);
    
    // 如果已经有监听器，先移除
    if (groupNameListeners[groupPub]) {
      groupNameListeners[groupPub].off();
    }
    
    // 监听群聊名称节点的变化
    const listener = gun.get(`group_name_${groupPub}`).on((data: any) => {
      if (!data || !data.name) return;
      
      console.log('📢 Group name changed:', { groupPub, newName: data.name });
      
      // 更新本地群聊列表中的名称
      const groupIndex = groups.value.findIndex(g => g.pub === groupPub);
      if (groupIndex >= 0) {
        groups.value[groupIndex].name = data.name;
        console.log('✅ Updated group name in local list');
      }
      
      // 更新会话列表中的名称
      const sessionIndex = groupSessions.value.findIndex(s => s.groupPub === groupPub);
      if (sessionIndex >= 0) {
        groupSessions.value[sessionIndex].groupName = data.name;
        console.log('✅ Updated group name in session list');
      }
      
      // 如果是当前群聊，更新当前群聊名称
      if (currentGroup.value === groupPub) {
        currentGroupName.value = data.name;
        console.log('✅ Updated current group name');
      }
      
      // 强制触发响应式更新
      groups.value = [...groups.value];
      groupSessions.value = [...groupSessions.value];
      
      // 保存到本地存储
      saveGroups();
      saveSessions();
    });
    
    groupNameListeners[groupPub] = listener;
    console.log('✅ Group name change listener set up successfully');
  };

  // 🆕 更新群聊名称（供群主使用）
  const updateGroupName = async (groupPub: string, newName: string): Promise<boolean> => {
    try {
      console.log('🎯 Updating group name:', { groupPub, newName });
      
      if (!newName.trim()) {
        console.error('❌ Group name cannot be empty');
        return false;
      }
      
      // 验证用户是否是群聊成员
      const group = groups.value.find(g => g.pub === groupPub);
      if (!group) {
        console.error('❌ Group not found');
        return false;
      }
      
      // 保存到Gun网络
      await saveGroupNameToNetwork(groupPub, newName.trim());
      
      // 立即更新本地状态
      const groupIndex = groups.value.findIndex(g => g.pub === groupPub);
      if (groupIndex >= 0) {
        groups.value[groupIndex].name = newName.trim();
      }
      
      const sessionIndex = groupSessions.value.findIndex(s => s.groupPub === groupPub);
      if (sessionIndex >= 0) {
        groupSessions.value[sessionIndex].groupName = newName.trim();
      }
      
      if (currentGroup.value === groupPub) {
        currentGroupName.value = newName.trim();
      }
      
      // 强制触发响应式更新
      groups.value = [...groups.value];
      groupSessions.value = [...groupSessions.value];
      
      // 保存到本地存储
      await saveGroups();
      await saveSessions();
      
      console.log('🎉 Group name updated successfully');
      
      const toast = await toastController.create({
        message: 'Group name updated successfully',
        duration: 2000,
        position: 'top',
        color: 'success',
      });
      await toast.present();
      
      return true;
    } catch (error) {
      console.error('❌ Failed to update group name:', error);
      
      const toast = await toastController.create({
        message: 'Failed to update group name',
        duration: 2000,
        position: 'top',
        color: 'danger',
      });
      await toast.present();
      
      return false;
    }
  };

  // 🔧 简化：创建群聊逻辑 - 添加群聊名称保存
  const createGroup = async () => {
   // console.log('🎯 开始创建群聊...');
    const groupNameTrimmed = newGroupName.value.trim();
    if (!groupNameTrimmed) {
     // console.error('❌ 群聊名称为空');
      throw new Error('群聊名称不能为空');
    }

    try {
     // console.log('🔐 生成群聊密钥对...');
      const pair = await Gun.SEA.pair();
      if (!pair || !pair.pub || !pair.priv) {
        throw new Error('密钥对生成失败');
      }
     // console.log('✅ 密钥对生成成功:', pair.pub);

      // 🆕 保存群聊名称到Gun网络
    //  console.log('💾 保存群聊名称到Gun网络...');
      await saveGroupNameToNetwork(pair.pub, groupNameTrimmed);

      const newGroup: GroupChatGroup = {
        name: groupNameTrimmed,
        pub: pair.pub,
        pair: pair
      };

    //  console.log('📝 添加群聊到列表...');
      
      // 🔧 直接添加到响应式数组
      groups.value.push(newGroup);
      tempKeyPair.value = pair;
      
     // console.log('📊 群聊创建成功!');
     // console.log('  群聊名称:', groupNameTrimmed);
     // console.log('  群聊Pub:', pair.pub);
     // console.log('  当前群聊总数:', groups.value.length);
      
      // 初始化群聊相关状态
      messagesByGroup.value[pair.pub] = [];
      membersByGroup.value[pair.pub] = [
        {
          pubKey: safeUserPub.value,
          alias: safeUserAlias.value,
          lastActive: Date.now(),
          joinedAt: Date.now(),
          isOnline: true
        }
      ];
      votesByGroup.value[pair.pub] = [];

      // 创建群聊会话
      const session: GroupChatSession = {
        groupPub: pair.pub,
        groupName: groupNameTrimmed,
        isRead: true,
        lastActivity: Date.now(),
      };
      
      groupSessions.value.push(session);
      
    //  console.log('💾 保存数据到localStorage...');
      
      // 🔧 立即保存到localStorage
      await saveGroups();
      await saveSessions();
      
      // 🆕 设置群聊名称变化监听器
      listenToGroupNameChanges(pair.pub);
      
    //  console.log('🎉 群聊创建和保存完成!');

      newGroupName.value = '';
      return newGroup;
    } catch (error) {
     // console.error('❌ 创建群聊失败:', error);
      tempKeyPair.value = null;
      throw error;
    }
  };

  const joinGroup = async () => {
    if (!joinGroupKey.value) {
      const toast = await toastController.create({
        message: 'please input',
        duration: 2000,
        position: 'top',
      });
      await toast.present();
      return;
    }
    try {
      const pair: GroupChatKeyPair = JSON.parse(joinGroupKey.value);
      if (!pair.pub || !pair.priv || !pair.epub || !pair.epriv) {
        throw new Error('Error keypair');
      }
      
      // 🆕 使用新的群聊名称获取机制
     // console.log('🔍 从Gun网络获取群聊名称...');
      const groupName = await getGroupNameFromNetwork(pair.pub);
     // console.log('📝 获取到群聊名称:', groupName);
      
      const newGroup: GroupChatGroup = {
        name: groupName,
        pub: pair.pub,
        pair,
      };
      if (groups.value.some((group) => group.pub === pair.pub)) {
        const toast = await toastController.create({
          message: 'Already',
          duration: 2000,
          position: 'top',
        });
        await toast.present();
        return;
      }
      await updateMemberActivity(pair.pub);
      await initializeVote(pair.pub);
      groups.value.push(newGroup);
      await saveGroups();
      joinGroupKey.value = '';
      messagesByGroup.value[pair.pub] = [];
      membersByGroup.value[pair.pub] = [];
      votesByGroup.value[pair.pub] = [];
      groupSessions.value.push({
        groupPub: pair.pub,
        groupName: groupName, // 使用从网络获取的群聊名称
        isRead: true,
        lastActivity: Date.now(),
      });
      groupSessions.value = [...groupSessions.value];
      await saveSessions(); // 添加await确保保存完成
      
      // 🆕 设置完整的监听器
      listenToGroupMessages(pair.pub);
      listenToGroupNameChanges(pair.pub);
      
      const toast = await toastController.create({
        message: 'Success',
        duration: 2000,
        position: 'top',
      });
      await toast.present();
    } catch (error) {
      const toast = await toastController.create({
        message: 'failed',
        duration: 2000,
        position: 'top',
      });
      await toast.present();
    }
  };

  const joinGroupWithKeyPair = async (keyPairString: string): Promise<GroupChatKeyPair | null> => {
    console.log('🎯 开始加入群聊...');
    try {
      const pair: GroupChatKeyPair = JSON.parse(keyPairString);
      if (!pair.pub || !pair.priv || !pair.epub || !pair.epriv) {
        throw new Error('无效的密钥对格式');
      }
      
    //  console.log('🔐 群聊密钥对验证成功:', pair.pub);
      
      // 🆕 使用新的群聊名称获取机制
    //  console.log('🔍 从Gun网络获取群聊名称...');
      const groupName = await getGroupNameFromNetwork(pair.pub);
      console.log('📝 获取到群聊名称:', groupName);
      
      const newGroup: GroupChatGroup = {
        name: groupName,
        pub: pair.pub,
        pair,
      };
      
      // 检查是否已经加入该群聊
      if (groups.value.some((group) => group.pub === pair.pub)) {
       // console.log('⚠️ 群聊已存在');
        const toast = await toastController.create({
          message: 'Already exist',
          duration: 2000,
          position: 'top',
        });
        await toast.present();
        return null;
      }
      
    
      // 更新成员活动和初始化投票
      await updateMemberActivity(pair.pub);
      await initializeVote(pair.pub);
      
      // 添加群聊到列表
      groups.value.push(newGroup);
     // console.log('✅ 群聊已添加到列表');
      
      // 🔧 修复：确保异步保存完成
      await saveGroups();
     // console.log('✅ 群聊保存完成');
      
      // 初始化群聊数据
      messagesByGroup.value[pair.pub] = [];
      membersByGroup.value[pair.pub] = [];
      votesByGroup.value[pair.pub] = [];
      
      // 🔧 修复：创建群聊会话并确保保存
      const session: GroupChatSession = {
        groupPub: pair.pub,
        groupName: groupName, // 使用从网络获取的群聊名称
        isRead: true,
        lastActivity: Date.now(),
      };
      
      groupSessions.value.push(session);
      groupSessions.value = [...groupSessions.value]; // 触发响应式更新
      
      // 🔧 修复：确保异步保存会话完成
      await saveSessions();
  
      // 🔧 修复：设置完整的监听器，确保群聊功能正常
     // console.log('🔊 开始设置群聊监听器...');
      listenToGroupMessages(pair.pub);
      listenToGroupMembers(pair.pub);
      listenToClearVotes(pair.pub);
      listenToClearSignal(pair.pub);
      
      // 🆕 设置群聊名称变化监听器
      listenToGroupNameChanges(pair.pub);
     // console.log('✅ 群聊监听器设置完成');
      
      // 🔧 修复：强制触发响应式更新，确保UI刷新
      groups.value = [...groups.value];
      groupSessions.value = [...groupSessions.value];
      
    
      const toast = await toastController.create({
        message: 'Success',
        duration: 2000,
        position: 'top',
      });
      await toast.present();
      
      return {
        pub: pair.pub,
        priv: pair.priv,
        epub: pair.epub,
        epriv: pair.epriv,
      };
    } catch (error) {
      console.error('❌ 加入群聊失败:', error);
      const toast = await toastController.create({
        message: 'Failed',
        duration: 2000,
        position: 'top',
      });
      await toast.present();
      return null;
    }
  };

  const deleteGroup = async (pub: string) => {
    try {
      await gun.get(`group_${pub}_members`).get(safeUserPub.value).put(null);
      await gun.get(`group_${pub}_clear_votes`).get(safeUserPub.value).put(null);
      groups.value = groups.value.filter((group) => group.pub !== pub);
      await saveGroups();
      groupSessions.value = groupSessions.value.filter((session) => session.groupPub !== pub);
      groupSessions.value = [...groupSessions.value];
      await saveSessions(); // 添加await确保保存完成
      if (currentGroup.value === pub) {
        currentGroup.value = null;
        currentGroupName.value = '';
        messagesByGroup.value[pub] = [];
        membersByGroup.value[pub] = [];
        votesByGroup.value[pub] = [];
        
        // 清理所有监听器，包括群聊名称监听器
        if (messageListeners[pub]) {
          messageListeners[pub].off();
          delete messageListeners[pub];
        }
        if (memberListeners[pub]) {
          memberListeners[pub].off();
          delete memberListeners[pub];
        }
        if (voteListeners[pub]) {
          voteListeners[pub].off();
          delete voteListeners[pub];
        }
        if (signalListeners[pub]) {
          signalListeners[pub].off();
          delete signalListeners[pub];
        }
        // 🆕 清理群聊名称监听器
        if (groupNameListeners[pub]) {
          groupNameListeners[pub].off();
          delete groupNameListeners[pub];
        }
        stopActivityUpdates();
      }
    } catch (error) {
      // Handle error silently
    }
  };

  const voteToClear = async () => {
    if (!currentGroup.value || !safeUserPub.value) return;
    try {
      await gun.get(`group_${currentGroup.value}_clear_votes`).get(safeUserPub.value).put({ agreed: true });
      const toast = await toastController.create({
        message: 'Voted to agree to clear the record',
        duration: 2000,
        position: 'top',
      });
      await toast.present();
    } catch (error) {
      // Handle error silently
    }
  };

  const cancelVote = async () => {
    if (!currentGroup.value || !safeUserPub.value) return;
    try {
      await gun.get(`group_${currentGroup.value}_clear_votes`).get(safeUserPub.value).put({ agreed: false });
      const toast = await toastController.create({
        message: 'Canceled consent to clear the record',
        duration: 2000,
        position: 'top',
      });
      await toast.present();
    } catch (error) {
      // Handle error silently
    }
  };

  const initiateClearChat = async () => {
    if (!currentGroup.value || !safeUserPub.value) return;
    try {
      const signalData: GroupChatClearSignal = {
        initiator: safeUserPub.value,
        timestamp: Date.now(),
        cleared: false,
      };
      await gun.get(`group_${currentGroup.value}_clear_signal`).put(signalData);
      const toast = await toastController.create({
        message: 'Cleaning up',
        duration: 2000,
        position: 'top',
      });
      await toast.present();
    } catch (error) {
      // Handle error silently
    }
  };

  const updateSessionPreview = (groupPub: string, message: GroupChatMessage) => {
    const sessionIndex = groupSessions.value.findIndex((s) => s.groupPub === groupPub);
    const group = groups.value.find((g) => g.pub === groupPub);
    if (!group) return;
    const isCurrentGroup = currentGroup.value === groupPub && router.currentRoute.value.path.includes(`/group/${groupPub}/messages`);
    const newSession = {
      groupPub,
      groupName: group.name,
      isRead: isCurrentGroup,
      lastActivity: Date.now(),
      previewMessage: { ...message },
    };
    if (sessionIndex >= 0) {
      groupSessions.value[sessionIndex] = newSession;
    } else {
      groupSessions.value.push(newSession);
    }
    groupSessions.value = [...groupSessions.value];
    saveSessions();
  };

  const markSessionAsRead = (groupPub: string) => {
    const session = groupSessions.value.find((s) => s.groupPub === groupPub);
    if (session && router.currentRoute.value.path.includes(`/group/${groupPub}/messages`)) {
      session.isRead = true;
      groupSessions.value = [...groupSessions.value];
      saveSessions();
    }
  };

  const loadGroupMessages = async (groupPub: string) => {
    const group = groups.value.find((g) => g.pub === groupPub);
    if (!group) return;
    const clearedData = await new Promise<any>((resolve) => {
      gun.get(`group_${groupPub}_cleared`).once((data) => resolve(data));
    });
    if (clearedData?.cleared) {
      messagesByGroup.value[groupPub] = [];
      const session = groupSessions.value.find((s) => s.groupPub === groupPub);
      if (session) {
        session.previewMessage = null;
        groupSessions.value = [...groupSessions.value];
        saveSessions();
      }
      return;
    }
    messagesByGroup.value[groupPub] = [];
    const node = gun.get(`group_${groupPub}`);
    const tempMessages: GroupChatMessage[] = [];
    node.map().once(async (data: any, id: string) => {
      if (!data) return;
      try {
        let messageText: string;
        if (data.encrypted) {
          if (decryptMessages.value) {
            const decrypted = await Gun.SEA.decrypt(data.encrypted, group.pair);
            messageText = decrypted ? String(decrypted) : '[Failed]';
          } else {
            messageText = String(data.encrypted);
          }
        } else if (data.text !== undefined) {
          messageText = String(data.text);
        } else {
          messageText = '[Null]';
        }
        const message: GroupChatMessage = {
          id,
          sender: data.sender || 'No Name',
          senderPub: data.senderPub || 'unknown',
          text: messageText,
          timestamp: data.timestamp || Date.now(),
          formattedTime: formatMessageTime(data.timestamp || Date.now()),
        };
        tempMessages.push(message);
        // Sort by timestamp ascending and take the latest 10
        tempMessages.sort((a, b) => a.timestamp - b.timestamp);
        if (tempMessages.length > 100) {
          tempMessages.shift();
        }
        messagesByGroup.value[groupPub] = [...tempMessages];
        if (tempMessages.length > 0) {
          lastMessageTimestamps[groupPub] = tempMessages[0].timestamp;
        }
      } catch (error) {
        const message: GroupChatMessage = {
          id,
          sender: data.sender || 'No Name',
          senderPub: data.senderPub || 'unknown',
          text: '[Failed]',
          timestamp: data.timestamp || Date.now(),
          formattedTime: formatMessageTime(data.timestamp || Date.now()),
        };
        tempMessages.push(message);
        tempMessages.sort((a, b) => a.timestamp - b.timestamp);
        if (tempMessages.length > 100) {
          tempMessages.shift();
        }
        messagesByGroup.value[groupPub] = [...tempMessages];
        if (tempMessages.length > 0) {
          lastMessageTimestamps[groupPub] = tempMessages[0].timestamp;
        }
      }
    });
    markSessionAsRead(groupPub);
  };

  const loadMoreMessages = async (groupPub: string) => {
    const group = groups.value.find((g) => g.pub === groupPub);
    if (!group) return;
    const lastTimestamp = lastMessageTimestamps[groupPub];
    if (!lastTimestamp) return;
    const node = gun.get(`group_${groupPub}`);
    const tempMessages: GroupChatMessage[] = [];
    let messageCount = 0;
    node.map().once(async (data: any, id: string) => {
      if (!data || messageCount >= 20) return;
      if (data.timestamp >= lastTimestamp) return;
      try {
        let messageText: string;
        if (data.encrypted) {
          if (decryptMessages.value) {
            const decrypted = await Gun.SEA.decrypt(data.encrypted, group.pair);
            messageText = decrypted ? String(decrypted) : '[Failed]';
          } else {
            messageText = String(data.encrypted);
          }
        } else if (data.text !== undefined) {
          messageText = String(data.text);
        } else {
          messageText = '[Null]';
        }
        const message: GroupChatMessage = {
          id,
          sender: data.sender || 'No Name',
          senderPub: data.senderPub || 'unknown',
          text: messageText,
          timestamp: data.timestamp || Date.now(),
          formattedTime: formatMessageTime(data.timestamp || Date.now()),
        };
        if (!messagesByGroup.value[groupPub].find((m) => m.id === message.id)) {
          tempMessages.push(message);
          messageCount++;
        }
      } catch (error) {
        const message: GroupChatMessage = {
          id,
          sender: data.sender || 'No Name',
          senderPub: data.senderPub || 'unknown',
          text: '[Failed]',
          timestamp: data.timestamp || Date.now(),
          formattedTime: formatMessageTime(data.timestamp || Date.now()),
        };
        if (!messagesByGroup.value[groupPub].find((m) => m.id === message.id)) {
          tempMessages.push(message);
          messageCount++;
        }
      }
    });
    if (tempMessages.length > 0) {
      tempMessages.sort((a, b) => a.timestamp - b.timestamp);
      messagesByGroup.value[groupPub] = [...tempMessages, ...messagesByGroup.value[groupPub]];
      lastMessageTimestamps[groupPub] = tempMessages[0].timestamp;
    }
    markSessionAsRead(groupPub);
  };

  const listenToGroupMessages = (groupPub: string) => {
    const group = groups.value.find((g) => g.pub === groupPub);
    if (!group) return;
    if (messageListeners[groupPub]) {
      messageListeners[groupPub].off();
    }
    const node = gun.get(`group_${groupPub}`);
    const listener = node.map().on(async (data: any, id: string) => {
      if (!data) return;
      try {
        let messageText: string;
        if (data.encrypted) {
          if (decryptMessages.value) {
            const decrypted = await Gun.SEA.decrypt(data.encrypted, group.pair);
            messageText = decrypted ? String(decrypted) : '[Failed]';
          } else {
            messageText = String(data.encrypted);
          }
        } else if (data.text !== undefined) {
          messageText = String(data.text);
        } else {
          messageText = '[Null]';
        }
        const message: GroupChatMessage = {
          id,
          sender: data.sender || 'No Name',
          senderPub: data.senderPub || 'unknown',
          text: messageText,
          timestamp: data.timestamp || Date.now(),
          formattedTime: formatMessageTime(data.timestamp || Date.now()),
        };
        if (!messagesByGroup.value[groupPub].find((m) => m.id === message.id)) {
          messagesByGroup.value[groupPub].push(message);
          // No need to sort, as new messages are appended and naturally newer
          updateSessionPreview(groupPub, message);
        }
      } catch (error) {
        const message: GroupChatMessage = {
          id,
          sender: data.sender || 'Null',
          senderPub: data.senderPub || 'unknown',
          text: '[Failed]',
          timestamp: Date.now(),
          formattedTime: formatMessageTime(Date.now()),
        };
        if (!messagesByGroup.value[groupPub].find((m) => m.id === message.id)) {
          messagesByGroup.value[groupPub].push(message);
          updateSessionPreview(groupPub, message);
        }
      }
    });
    messageListeners[groupPub] = listener;
  };

  const listenToGroupMembers = (groupPub: string) => {
    membersByGroup.value[groupPub] = [];
    const group = groups.value.find((g) => g.pub === groupPub);
    if (!group) return;
    if (memberListeners[groupPub]) {
      memberListeners[groupPub].off();
    }
    const listener = gun
      .get(`group_${groupPub}_members`)
      .map()
      .on((data: any, pubKey: string) => {
        if (!data) {
          membersByGroup.value[groupPub] = membersByGroup.value[groupPub].filter((m) => m.pubKey !== pubKey);
          return;
        }
        const isGroupMember = groups.value.some((g) => g.pub === groupPub);
        if (!isGroupMember) return;
        const lastActive = data.lastActive || 0;
        const isOnline = Date.now() - lastActive < 60000;
        const member: GroupChatMember = {
          pubKey,
          alias: data.alias || 'No Name',
          lastActive,
          joinedAt: data.joinedAt || Date.now(),
          isOnline,
        };
        const existingIndex = membersByGroup.value[groupPub].findIndex((m) => m.pubKey === pubKey);
        if (existingIndex >= 0) {
          membersByGroup.value[groupPub][existingIndex] = member;
        } else {
          membersByGroup.value[groupPub].push(member);
        }
        membersByGroup.value[groupPub].sort((a, b) => a.joinedAt - b.joinedAt);
      });
    memberListeners[groupPub] = listener;
  };

  const listenToClearVotes = (groupPub: string) => {
    votesByGroup.value[groupPub] = [];
    const group = groups.value.find((g) => g.pub === groupPub);
    if (!group) return;
    if (voteListeners[groupPub]) {
      voteListeners[groupPub].off();
    }
    const listener = gun
      .get(`group_${groupPub}_clear_votes`)
      .map()
      .on((data: any, pubKey: string) => {
        if (!data) {
          votesByGroup.value[groupPub] = votesByGroup.value[groupPub].filter((v) => v.voterId !== pubKey);
          return;
        }
        const vote: GroupChatVote = {
          voterId: pubKey,
          vote: data.agreed ? 'agree' : 'disagree',
          timestamp: Date.now(),
        };
        const existingIndex = votesByGroup.value[groupPub].findIndex((v) => v.voterId === pubKey);
        if (existingIndex >= 0) {
          votesByGroup.value[groupPub][existingIndex] = vote;
        } else {
          votesByGroup.value[groupPub].push(vote);
        }
      });
    voteListeners[groupPub] = listener;
  };

  const listenToClearSignal = (groupPub: string) => {
    if (signalListeners[groupPub]) {
      signalListeners[groupPub].off();
    }
    const listener = gun
      .get(`group_${groupPub}_clear_signal`)
      .on(async (data: GroupChatClearSignal | null) => {
        if (!data || data.cleared) return;
        const selfMember = (membersByGroup.value[groupPub] || []).find((m) => m.pubKey === safeUserPub.value);
        if (!selfMember || !selfMember.isOnline) return;
        await executeClearChat(groupPub);
      });
    signalListeners[groupPub] = listener;
  };

  const executeClearChat = async (groupPub: string) => {
    try {
      await gun.get(`group_${groupPub}`).put(null);
      await gun.get(`group_${groupPub}_clear_signal`).put({
        initiator: safeUserPub.value,
        timestamp: Date.now(),
        cleared: true,
      });
      await gun.get(`group_${groupPub}_cleared`).put({
        cleared: true,
        timestamp: Date.now(),
      });
      messagesByGroup.value[groupPub] = [];
      lastMessageTimestamps[groupPub] = undefined;
      const session = groupSessions.value.find((s) => s.groupPub === groupPub);
      if (session) {
        session.previewMessage = null;
        groupSessions.value = [...groupSessions.value];
        saveSessions();
      }
      const toast = await toastController.create({
        message: 'Cleared',
        duration: 2000,
        position: 'top',
      });
      await toast.present();
    } catch (error) {
      // Handle error silently
    }
  };

  const selectGroup = (pub: string) => {
    if (currentGroup.value && currentGroup.value !== pub) {
      if (memberListeners[currentGroup.value]) {
        memberListeners[currentGroup.value].off();
        delete memberListeners[currentGroup.value];
      }
      if (voteListeners[currentGroup.value]) {
        voteListeners[currentGroup.value].off();
        delete voteListeners[currentGroup.value];
      }
      if (signalListeners[currentGroup.value]) {
        signalListeners[currentGroup.value].off();
        delete signalListeners[currentGroup.value];
      }
      stopActivityUpdates();
    }
    currentGroup.value = pub;
    const group = groups.value.find((g) => g.pub === pub);
    currentGroupName.value = group ? group.name : '';
    messagesByGroup.value[pub] = messagesByGroup.value[pub] || [];
    membersByGroup.value[pub] = membersByGroup.value[pub] || [];
    votesByGroup.value[pub] = votesByGroup.value[pub] || [];
    loadGroupMessages(pub);
    listenToGroupMessages(pub);
    listenToGroupMembers(pub);
    listenToClearVotes(pub);
    listenToClearSignal(pub);
    updateMemberActivity(pub);
    startActivityUpdates(pub);
    markSessionAsRead(pub);
  };

  const setCurrentGroup = (pub: string | null) => {
    if (pub) {
      selectGroup(pub);
    } else {
      currentGroup.value = null;
      currentGroupName.value = '';
      stopActivityUpdates();
    }
  };

  const getCurrentGroup = () => {
    return currentGroup.value;
  };

  const sendMessage = async (messageContent: string = newMessage.value) => {
    const ensuredStringContent = String(messageContent);
    if (!messageContent || !currentGroup.value) return;
    const group = groups.value.find((g) => g.pub === currentGroup.value);
    if (!group) return;
    const isGroupMember = groups.value.some((g) => g.pub === currentGroup.value);
    if (!isGroupMember) {
      const toast = await toastController.create({
        message: 'Not joined',
        duration: 2000,
        position: 'top',
      });
      await toast.present();
      return;
    }
    try {
      const messageData: any = {
        sender: safeUserAlias.value,
        senderPub: safeUserPub.value,
        timestamp: Date.now(),
      };
   
      if (encryptMessages.value) {
        messageData.encrypted = await Gun.SEA.encrypt(ensuredStringContent, group.pair);
      } else {
        messageData.text = ensuredStringContent;
      }
      const node = gun.get(`group_${currentGroup.value}`);
      await node.set(messageData);
      const message: GroupChatMessage = {
        id: Date.now().toString(),
        sender: safeUserAlias.value,
        senderPub: safeUserPub.value,
        text: ensuredStringContent,
        timestamp: Date.now(),
        formattedTime: formatMessageTime(Date.now()),
        status: 'sent',
        isSending: false,
      };
      messagesByGroup.value[currentGroup.value].push(message);
      updateSessionPreview(currentGroup.value, message);
      newMessage.value = '';
    } catch (error) {
      const message: GroupChatMessage = {
        id: Date.now().toString(),
        sender: safeUserAlias.value,
        senderPub: safeUserPub.value,
        text: ensuredStringContent,
        timestamp: Date.now(),
        formattedTime: formatMessageTime(Date.now()),
        status: 'failed',
        isSending: false,
      };
      messagesByGroup.value[currentGroup.value].push(message);
      updateSessionPreview(currentGroup.value, message);
      newMessage.value = '';
    }
  };

  const sendImage = async (messageContent: string = newMessage.value) => {
    if (!messageContent || !currentGroup.value) return;
    const group = groups.value.find((g) => g.pub === currentGroup.value);
    if (!group) return;
    const isGroupMember = groups.value.some((g) => g.pub === currentGroup.value);
    if (!isGroupMember) {
      const toast = await toastController.create({
        message: 'Not joined',
        duration: 2000,
        position: 'top',
      });
      await toast.present();
      return;
    }
    try {
      const messageData: any = {
        sender: safeUserAlias.value,
        senderPub: safeUserPub.value,
        timestamp: Date.now(),
      };
      const ensuredStringContent = String(messageContent);
      if (encryptMessages.value) {
        messageData.encrypted = await Gun.SEA.encrypt(ensuredStringContent, group.pair);
      } else {
        messageData.text = ensuredStringContent;
      }
      const node = gun.get(`group_${currentGroup.value}`);
      await node.set(messageData);
      const message: GroupChatMessage = {
        id: Date.now().toString(),
        sender: safeUserAlias.value,
        senderPub: safeUserPub.value,
        text: ensuredStringContent,
        timestamp: Date.now(),
        formattedTime: formatMessageTime(Date.now()),
        status: 'sent',
        isSending: false,
      };
      messagesByGroup.value[currentGroup.value].push(message);
      updateSessionPreview(currentGroup.value, message);
      newMessage.value = '';
    } catch (error) {
         const ensuredStringContent = String(messageContent);
      const message: GroupChatMessage = {
        id: Date.now().toString(),
        sender: safeUserAlias.value,
        senderPub: safeUserPub.value,
        text: ensuredStringContent,
        timestamp: Date.now(),
        formattedTime: formatMessageTime(Date.now()),
        status: 'failed',
        isSending: false,
      };
      messagesByGroup.value[currentGroup.value].push(message);
      updateSessionPreview(currentGroup.value, message);
      newMessage.value = '';
    }
  };

  // 🆕 群聊相关监听器的完整设置
  const setupAllGroupListeners = (groupPub: string) => {
    console.log('🔊 设置群聊完整监听器:', groupPub);
    listenToGroupMessages(groupPub);
    listenToGroupMembers(groupPub);
    listenToClearVotes(groupPub);
    listenToClearSignal(groupPub);
    listenToGroupNameChanges(groupPub);
    console.log('✅ 群聊完整监听器设置完成');
  };

  // 移除了 onMounted，因为它会在每个使用此 composable 的组件中都执行
  // 现在由 Card.vue 组件的 onMounted 负责调用 loadGroups()

  watch(groups, () => {
    groups.value.forEach(group => {
      if (!messageListeners[group.pub]) {
        setupAllGroupListeners(group.pub); // 使用完整的监听器设置
      }
    });
  }, { deep: true });

  watch(decryptMessages, () => {
    if (currentGroup.value) {
      loadGroupMessages(currentGroup.value);
    }
  });

  onUnmounted(() => {
    stopActivityUpdates();
    Object.values(messageListeners).forEach((listener) => listener.off());
    Object.values(memberListeners).forEach((listener) => listener.off());
    Object.values(voteListeners).forEach((listener) => listener.off());
    Object.values(signalListeners).forEach((listener) => listener.off());
    // 🆕 清理群聊名称监听器
    Object.values(groupNameListeners).forEach((listener) => listener.off());
  });

  return {
    newGroupName,
    joinGroupKey,
    groups,
    currentGroup,
    currentGroupName,
    messagesByGroup,
    membersByGroup,
    votesByGroup,
    newMessage,
    tempKeyPair,
    encryptMessages,
    decryptMessages,
    agreeCount,
    canClearChat,
    copyKeyPair,
    loadGroups,
    createGroup,
    joinGroup,
    deleteGroup,
    voteToClear,
    cancelVote,
    initiateClearChat,
    selectGroup,
    setCurrentGroup,
    getCurrentGroup,
    sendMessage,
    safeUserAlias,
    safeUserPub,
    sendImage,
    joinGroupWithKeyPair,
    groupSessions,
    markSessionAsRead,
    loadGroupMessages,
    loadMoreMessages,
    triggerLightHaptic,
    // 🆕 群聊名称相关功能
    updateGroupName,
    getGroupNameFromNetwork,
    listenToGroupNameChanges,
    saveGroupNameToNetwork,
  };
}; 